import type { UiMenuGroup, UiScreenActions } from '@/types/ui';
import type { CompanyResponse } from '@/types/company';
import type { HrEmploymentType, HrLeaveType, HrOrgStructure } from '@/types/hr';
import type { MasterOption } from '@/api/masterData.api';
import type { RoleResponse } from '@/types/role';
import type { StoreResponse } from '@/types/store';
import type { ScreenActionGrants } from '@/utils/screenPermissionMatrix';

export interface SessionLocation {
  id: number;
  code: string;
  name: string;
  type: string;
  companyId?: number;
  currencyCode?: string;
  countryCode?: string;
}

export interface SessionCompanyScope {
  companies: CompanyResponse[];
  defaultCompanyId?: number | null;
  activeCompanyId?: number | null;
}

export interface SessionReferenceData {
  countries: MasterOption[];
  currencies: MasterOption[];
  timezones: MasterOption[];
  taxModes: MasterOption[];
  languages?: MasterOption[];
}

export type HrEmployeeDataScope = 'COMPANY' | 'TEAM' | 'SELF';
export type HrHierarchyScope = 'COMPANY' | 'MY_TEAM';

export interface SessionHrMasters {
  departments: HrOrgStructure['departments'];
  designations: HrOrgStructure['designations'];
  businessUnits: HrOrgStructure['businessUnits'];
  grades: HrOrgStructure['grades'];
  leaveTypes: HrLeaveType[];
  employmentTypes: HrEmploymentType[];
  /** From permission data_scope — not role codes. */
  hierarchyScope?: HrHierarchyScope;
  employeeDataScope?: HrEmployeeDataScope;
  companyWideEmployeeAccess?: boolean;
}

export interface SessionModuleEntry {
  slug: string;
  erpModule: string;
  label: string;
  homePath: string;
}

export interface SessionMenuManifest {
  enabledModules: string[];
  accessibleModules: string[];
  enabledFeatures: string[];
  permissions: string[];
  roleCode?: string | null;
  masterAdmin: boolean;
  launcherModules: SessionModuleEntry[];
  /** Backend-driven navigation groups */
  navigation?: UiMenuGroup[];
  /** Path → screen actions from backend */
  screenActions?: Record<string, UiScreenActions>;
  /** Role uses stored screen grants — flat permissions must not override UI access. */
  screenGrantsConfigured?: boolean;
  /** Stored screen grants for module-scoped settings visibility. */
  screenGrants?: ScreenActionGrants;
}

/** Full bootstrap payload from GET /v1/session/init */
export interface SessionInitResponse {
  userId: number;
  userUuid: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: number | null;
  organizationName?: string | null;
  organizationUuid?: string | null;
  storeId?: number | null;
  roleCode?: string | null;
  roleName?: string | null;
  masterAdmin: boolean;
  permissions: string[];
  enabledFeatures?: string[];
  enabledModules?: string[];
  accessibleModules?: string[];
  sessionUuid?: string | null;
  mfaEnabled?: boolean;
  entryModule?: string | null;
  forcePasswordChange?: boolean;
  profilePictureUrl?: string | null;
  companyScope?: SessionCompanyScope;
  locations?: SessionLocation[];
  stores?: StoreResponse[];
  roles?: RoleResponse[];
  referenceData?: SessionReferenceData;
  hr?: SessionHrMasters | null;
  menu?: SessionMenuManifest;
  settings?: SessionSettings;
  featureFlags?: SessionFeatureFlags;
  cacheHints?: Record<string, number>;
  generatedAt?: string;
}

export interface SessionSettings {
  locale?: string;
  languageCode?: string;
  /** Optional user override; null when inheriting company language. */
  userLanguageCode?: string | null;
  companyLanguageCode?: string;
  languageSource?: 'USER' | 'COMPANY' | 'SYSTEM';
  languageDirection?: 'ltr' | 'rtl';
  displayLocale?: string;
  /** Company timezone (business calendar). */
  timezone?: string;
  /** Optional user override. */
  userTimezone?: string | null;
  /** Resolved display timezone: user ?? company ?? UTC. */
  activeTimezone?: string;
  timezoneSource?: 'USER' | 'COMPANY' | 'UTC';
  dateFormat?: string;
  timeFormat?: string;
  dateTimeFormat?: string;
  currencyCode?: string | null;
  subscriptionValidationEnabled?: boolean;
  orgPreferences?: Record<string, unknown>;
}

export interface SessionFeatureFlags {
  enabledFeatures?: string[];
  enabledModules?: string[];
  accessibleModules?: string[];
  realtimeEnabled?: boolean;
  backendDrivenUi?: boolean;
}

export function hrMastersToOrgStructure(hr?: SessionHrMasters | null): HrOrgStructure | null {
  if (!hr) return null;
  return {
    departments: hr.departments ?? [],
    designations: hr.designations ?? [],
    businessUnits: hr.businessUnits ?? [],
    grades: hr.grades ?? [],
  };
}
